import React, { useState, useEffect, useCallback } from 'react'
import PropTypes, { arrayOf, func } from 'prop-types'
import { css } from '@emotion/core'
import { Bleed, ComboMenu, Image, List, ListItem, colors } from '@fs/zion-ui'
import { formatValue } from '@fs/zion-locale'
import { ContentCheck } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { FamilyGroupType } from '../../sharedPropTypes/familyGroupProps'
import { getSelectedGroupPoints } from '../../helpers/leaderboard_helper'
import PersonAvatar from '../PersonAvatar'
import star from '../../assets/icons/verified.svg'
import CloudBackground from './CloudBackground'
import { sessionizeUrl } from '../../helpers/url_helper'
import { leaderboardPersonProps } from '../../sharedPropTypes/personProps'
import { NameBlock } from './NameBlock'
import { ListItemContent } from './ListItemContent'
import AccessibleContent from '../AccessibleContent'

// -------------------------
// Styles
// -------------------------
// Layout styles
const sectionCss = css`
  min-height: 276px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-end;
  color: ${colors.yellow70};
  position: relative;
`

const columnCss = css`
  display: flex;
  flex-direction: column;
  position: relative;
`

const firstPlaceCss = css`
  ${columnCss};
  order: 2;
`

const secondPlaceCss = css`
  ${columnCss};
  order: 1;
`

const thirdPlaceCss = css`
  ${columnCss};
  order: 3;
`

// Component-specific styles
const menuCss = css`
  width: 100%;
  max-width: 250px;
  margin: auto;
`

const listCss = css`
  background-color: ${colors.gray00};
  text-align: initial;
  margin: 6px 0;
  border-radius: 100px;
  contain: content;
  padding: 0 12px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

const pointsCss = css`
  color: ${colors.yellow70};
  font-weight: bold;
  font-size: 14px;
  margin: 0;
`

const pillarCss = css`
  position: relative;
  width: 107px;
  height: 104px;
  margin: 0 4px;
  border-radius: 8px;
  background: linear-gradient(180deg, #e68c03 0%, rgba(241, 171, 13, 0) 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
`

const avatarCss = css`
  position: relative;
  margin: 0 -8px;
  top: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  flex-direction: column;
`

const secondThirdCss = css`
  border: 4px solid white;
  border-radius: 48px;
  background-color: ${colors.yellow05};
  height: 96px;
  width: 96px;
`

const firstCss = css`
  border: 4px solid white;
  border-radius: 72px;
  background-color: ${colors.yellow05};
  margin: 0 -4px;
`

const nameblockCss = css`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 8px;
  padding-inline-start: 2px;
  padding-inline-end: 2px;
  max-width: 100%;
`

// -------------------------
// Helper Functions
// -------------------------
const getMonogram = (person) => person?.userProfileName?.charAt(0)

const createGroupOption = (group, selectedOption) => {
  const isSelected = selectedOption?.value?.familyGroupId === group?.familyGroupId
  return {
    startElement: isSelected ? <ListItem.Icon Icon={ContentCheck} /> : null,
    primaryText: isSelected ? <b>{selectedOption?.name}</b> : group?.name,
    name: isSelected ? selectedOption?.name : group?.name,
    value: group,
  }
}

// -------------------------
// Stable Helper Components
// -------------------------

// StarNumber now lives at the top level.
const StarNumber = ({ size = 24, number = 1 }) => {
  const getFontSize = () => {
    if (size > 58) return '34px'
    if (size > 30 && size < 58) return '20px'
    return '12px'
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 4,
      }}
      aria-hidden="true" // Hide from screen readers as we'll provide context elsewhere
    >
      <Image
        alt=""
        maxHeight={size}
        maxWidth={size}
        height={size}
        width={size}
        src={star}
        style={{
          filter: size > 30 ? 'drop-shadow(0px 0px 14px #C85704)' : undefined,
          overflow: 'visible',
        }}
      />
      <span
        style={{
          position: 'absolute',
          fontWeight: 'bold',
          fontSize: getFontSize(),
          color: '#621c00',
        }}
      >
        {number}
      </span>
    </div>
  )
}

StarNumber.propTypes = {
  size: PropTypes.number,
  number: PropTypes.number,
}

const Top3 = ({ people }) => {
  const { t } = useTranslation()

  return (
    <div css={sectionCss}>
      <CloudBackground />
      {/* First Place - First in DOM for accessibility */}
      <div css={firstPlaceCss}>
        <div css={avatarCss} style={{ top: '16px' }}>
          <div css={firstCss}>
            <PersonAvatar size="xl" src={sessionizeUrl(people[0]?.portraitUrl)} monogram={getMonogram(people[0])} />
          </div>
          <div style={{ position: 'absolute', bottom: '-38px' }}>
            <StarNumber size={62} number={1} />
          </div>
        </div>
        <div css={pillarCss} style={{ height: '132px', width: '112px' }}>
          <AccessibleContent
            accessibleLabel={t('leaderboard.position.first.a11y', '{name} in first place with {points} points', {
              name: people[0]?.userProfileName || '',
              points: formatValue(people[0]?.points) || 0,
            })}
          >
            <NameBlock person={people[0]} css={nameblockCss} />
          </AccessibleContent>
        </div>
      </div>

      {/* Second Place */}
      <div css={secondPlaceCss}>
        <div css={avatarCss} aria-hidden>
          <div css={secondThirdCss}>
            {people[1] && (
              <PersonAvatar size="lg" src={sessionizeUrl(people[1]?.portraitUrl)} monogram={getMonogram(people[1])} />
            )}
          </div>
          <div style={{ position: 'absolute', bottom: '-20px' }}>
            <StarNumber size={36} number={2} />
          </div>
        </div>
        <div css={pillarCss}>
          <AccessibleContent
            accessibleLabel={t('leaderboard.position.second.a11y', '{name} in second place with {points} points', {
              name: people[1]?.userProfileName || '',
              points: formatValue(people[1]?.points) || 0,
            })}
          >
            <NameBlock css={nameblockCss} person={people[1]} />
          </AccessibleContent>
        </div>
      </div>

      {/* Third Place */}
      <div css={thirdPlaceCss}>
        <div css={avatarCss}>
          <div css={secondThirdCss}>
            {people[2] && (
              <PersonAvatar size="lg" src={sessionizeUrl(people[2]?.portraitUrl)} monogram={getMonogram(people[2])} />
            )}
          </div>
          <div style={{ position: 'absolute', bottom: '-20px' }} aria-hidden>
            <StarNumber size={36} number={3} />
          </div>
        </div>
        <div css={pillarCss}>
          <AccessibleContent
            accessibleLabel={t('leaderboard.position.third.a11y', '{name} in third place with {points} points', {
              name: people[2]?.userProfileName || '',
              points: formatValue(people[2]?.points) || 0,
            })}
          >
            <NameBlock person={people[2]} />
          </AccessibleContent>
        </div>
      </div>
    </div>
  )
}

Top3.propTypes = {
  people: arrayOf(leaderboardPersonProps),
}

// -------------------------
// Components for ListItem Icons
// -------------------------
const AvatarIcon = ({ person, size = 'sm' }) => (
  <PersonAvatar src={sessionizeUrl(person?.portraitUrl)} monogram={person?.userProfileName?.charAt(0)} size={size} />
)

AvatarIcon.propTypes = {
  person: leaderboardPersonProps,
  size: PropTypes.string,
}

const PointsIcon = ({ points }) => <p css={pointsCss}>{formatValue(points)}</p>

PointsIcon.propTypes = {
  points: PropTypes.number,
}

const LeaderboardListItem = ({ person, index }) => {
  const { t } = useTranslation()

  if (person?.status === 'PENDING') return null

  const position = index + 4 // Since these come after the top 3

  return (
    <div key={person?.memberCisId} css={listCss}>
      <StarNumber number={position} />
      <div style={{ flexGrow: 1 }}>
        <AccessibleContent
          accessibleLabel={t('leaderboard.position.other', '{name} in position {position} with {points} points', {
            name: person?.userProfileName,
            position,
            points: formatValue(person?.points),
          })}
        >
          <ListItemContent person={person} AvatarIcon={AvatarIcon} PointsIcon={PointsIcon} />
        </AccessibleContent>
      </div>
    </div>
  )
}

LeaderboardListItem.propTypes = {
  person: leaderboardPersonProps,
  index: PropTypes.number.isRequired,
}

const Leaderboard = ({ familyGroups, familyGroup, setFamilyGroup }) => {
  const { t } = useTranslation()
  const [top3, setTop3] = useState([])
  const [selectedGroupPoints, setSelectedGroupPoints] = useState([])
  const [options, setOptions] = useState([])
  const [selectedOption, setSelectedOption] = useState({
    startElement: <ListItem.Icon Icon={ContentCheck} />,
    primaryText: <b>{familyGroup?.name}</b>,
    name: familyGroup?.name,
    value: familyGroup,
  })

  // Get familyGroups + ALL group as drop-down filter options
  useEffect(() => {
    if (familyGroups?.length > 1) {
      setOptions(
        familyGroups
          .filter((group) => group?.isGroup || group?.familyGroupId === 'ALL')
          .map((group) => createGroupOption(group, selectedOption))
      )
    }
  }, [familyGroups, selectedOption])

  useEffect(() => {
    if (familyGroup) {
      setSelectedOption({
        startElement: null,
        primaryText: <b>{familyGroup?.name}</b>,
        name: familyGroup?.name,
        value: familyGroup,
      })
    }
  }, [familyGroup])

  useEffect(() => {
    if (familyGroup && familyGroups) {
      const newGroupPoints = getSelectedGroupPoints({
        selectedGroupId: familyGroup?.familyGroupId,
        items: familyGroups,
      })
      const top3People = newGroupPoints.slice(0, 3)
      const remainingPeople = newGroupPoints.slice(3)
      setTop3(top3People)
      setSelectedGroupPoints(remainingPeople)
    }
  }, [familyGroups, familyGroup])

  const handleOptionChange = useCallback(
    (opt) => {
      setFamilyGroup(opt.value)
    },
    [setFamilyGroup]
  )

  return (
    <div aria-label={t('leaderboard.container.a11y', 'Group rankings')}>
      <div aria-live="assertive" css={menuCss}>
        <AccessibleContent
          accessibleLabel={t('leaderboard.select-family-group.a11y.label', 'Select a family group to view leaderboard')}
        />

        <ComboMenu
          fullWidth
          options={options}
          selectedOption={selectedOption}
          setSelectedOption={handleOptionChange}
          aria-labelledby="leaderboard-group-selector-label"
        />
        <Bleed sides>
          <Top3 people={top3} />
        </Bleed>
      </div>
      <List>
        {selectedGroupPoints.map((person, i) => (
          <LeaderboardListItem key={person?.memberCisId} person={person} index={i} />
        ))}
      </List>
    </div>
  )
}

Leaderboard.propTypes = {
  familyGroup: FamilyGroupType,
  familyGroups: arrayOf(FamilyGroupType),
  setFamilyGroup: func,
}

export default Leaderboard
